import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ClaimProductType, ClaimType } from 'src/types/claim';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { checkPermission, useCheckPermission } from 'src/utils/checkPermission';
import { CLAIM_PROPERTY_EDIT_KEYS } from 'skladon-types';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import {
    clearClaimDetail,
    createClaimDetailFetch,
    editClaimDetailEshopNoteFetch,
    getClaimDetailChangelogFetch,
    getClaimDetailFetch,
    patchClaimDetailFetch,
    updateClaimDetailFetch
} from 'src/resources/claimDetail/reducer';
import RenderComponents from './RenderComponents';
import {
    getAllowedStatuses,
    stripErrorLogFields,
    validateClaimInvoiceFile,
    validateErrorLogFields,
} from './functions';
import { FileType } from 'src/types/file';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';
import {
    buildStatusCancelPatchPayload,
    CLAIM_CANCEL_STATUS,
    isCancelStatusChange,
} from 'src/utils/cancelStatus';
import { getOrderClaims } from '../AllOrdersContainer/functions';
import { buildClaimExistsDialogParams } from './claimExistsDialog';

const initData = {};
const ClaimDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idClaim, idOrder } = useParams();

    const newClaim = !idClaim;

    const formRef = useRef<HTMLFormElement>(null);
    const productsFormRef = useRef<HTMLFormElement>(null);
    const errorLogFormRef = useRef<HTMLFormElement>(null);
    const data = useSelector((state: RootStoreType) => state.claimDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.claimDetail.changelog);
    const loading = useSelector((state: RootStoreType) => state.claimDetail.loading);
    const saveInFlightRef = useRef(false);
    const claimStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimStatusCodes);
    const claimStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.claimStatusCodes);
    const claimReasonCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimReasonCodes);
    const claimReasonCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.claimReasonCodes);
    const claimsResolverCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimsResolverCodes);
    const claimsResolverCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.claimsResolverCodes
    );
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const lineStateCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.lineStateCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<ClaimType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>('detail');
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('CLAIM.CREATE');
    const permissionCreateMultipleClaims = useCheckPermission('CLAIM.CREATE_MULTIPLE');
    const permissionUpdate = useCheckPermission('CLAIM.UPDATE');
    const permissionEditAll = useCheckPermission('CLAIM.EDITALL');
    const permissionEditStatus = data?.STATUS && checkPermission(`CLAIM.STATUS.EDIT.${data.STATUS.toUpperCase()}`);
    const permissionCreateOrder = useCheckPermission('ORDER.CREATE');
    const permissionViewErrorLog = useCheckPermission('CLAIM.ERROR_LOG.VIEW');
    const permissionEditErrorLog = useCheckPermission('CLAIM.ERROR_LOG.EDIT');
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const showErrorLogTab = !!skladonUser && permissionViewErrorLog;
    const canEditErrorLog = !!skladonUser && (permissionEditErrorLog || permissionEditAll);

    const claimPropertyPermissions = useMemo(() => {
        const statusUpper = data?.STATUS?.toUpperCase();
        if (!statusUpper) {
            return {} as Record<string, boolean>;
        }
        return CLAIM_PROPERTY_EDIT_KEYS.reduce(
            (acc, key) => {
                acc[key] = checkPermission(`CLAIM.PROPERTIES.${key}.EDIT.${statusUpper}`);
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [data?.STATUS]);

    const disableEdit =
        !permissionEditAll && (newClaim ? !permissionCreate : !permissionEditStatus || !permissionUpdate);
    const isAttributeEditDisabled = (attributeKey: string) =>
        Boolean(disableEdit && !permissionEditAll && !claimPropertyPermissions[attributeKey]);
    const canEditAnyClaimProperty = Object.values(claimPropertyPermissions).some(Boolean);
    const allowedStatuses = getAllowedStatuses(claimStatusCodes, claimStatusCodesObject, data);
    const canEditProducts = !!skladonUser || (!skladonUser && (newClaim || editData.STATUS === 'received'));
    const backUrl = location.pathname.indexOf('orders') === 1 ? `/orders/${idOrder}/` : '/claims/';

    useEffect(() => {
        if (idClaim) {
            dispatch(setLocationParams({ name: translate('claimDetail.title', { idClaim }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('claimDetail.titleNew'), backUrl }));
        }
    }, [idClaim, backUrl, locationHistory]);

    useEffect(() => {
        if (idClaim) {
            dispatch(getClaimDetailFetch(idClaim));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearClaimDetail());
        };
    }, [idClaim]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idClaim && data?.ID) {
            navigate('/claims/' + String(data.ID));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        if (!changelog && activeTab === 'history') {
            dispatch(getClaimDetailChangelogFetch(idClaim));
        }
    }, [activeTab, changelog]);

    useEffect(() => {
        if (!loading) {
            saveInFlightRef.current = false;
        }
    }, [loading]);

    // save edit data -start
    const editDataRef = useRef<ClaimType>({});
    useEffect(() => {
        if (
            newClaim &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/claims') ||
                samePaths(locationHistory[1]?.pathname, '/claims/new'))
        ) {
            setConfirmParams({
                title: translate('claimDetail.confirmGetNewDataStorage'),
                content: translate('claimDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: ClaimType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };

        if (key === 'FILES') {
            const files = value as FileType[];
            if (files.some((file) => file.type === 'invoice')) {
                delete newErrors.invoice;
            }
        }
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProduct: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        if (value.length) {
            delete newErrors.PRODUCTS;
        } else {
            newErrors.PRODUCTS = true;
        }
        newData[key] = value;
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleCreateOrder = () => {
        navigate(`/orders/${data?.ORDER_ID}/clone`);
    };

    const handleSave = async () => {
        if (saveInFlightRef.current || loading) {
            return false;
        }
        if (disableEdit && !canEditAnyClaimProperty && !(activeTab === 'errorLogging' && canEditErrorLog)) {
            saveInFlightRef.current = true;
            dispatch(editClaimDetailEshopNoteFetch({ idClaim, data: { ESHOP_NOTE: editData.ESHOP_NOTE || '' } }));
            return false;
        }

        if (isCancelStatusChange(editData.STATUS, newClaim, CLAIM_CANCEL_STATUS)) {
            saveInFlightRef.current = true;
            dispatch(
                patchClaimDetailFetch({
                    idClaim,
                    data: buildStatusCancelPatchPayload(CLAIM_CANCEL_STATUS),
                })
            );
            return false;
        }

        const newFormErrors =
            activeTab === 'errorLogging' && canEditErrorLog ?
                validateErrorLogFields(editData) :
                checkFormValidity(formRef);

        if (!(activeTab === 'errorLogging' && canEditErrorLog) && !isAttributeEditDisabled('FILES')) {
            Object.assign(newFormErrors, validateClaimInvoiceFile(editData));
        }

        if (!Object.keys(newFormErrors).length) {
            const { NOTE, ...editDataWithoutNote } = editData;
            let formatData = {
                ...(skladonUser ? editData : editDataWithoutNote),
                PRODUCTS: editData.PRODUCTS?.map((product) => {
                    const newProduct: ClaimProductType = { ...product };
                    if (newProduct.ID && newProduct.ID < 0) {
                        delete newProduct.ID;
                    }
                    return { ...newProduct };
                })
            };

            if (!canEditErrorLog) {
                formatData = stripErrorLogFields(formatData as ClaimType) as typeof formatData;
            }

            const submitCreate = () => {
                saveInFlightRef.current = true;
                dispatch(createClaimDetailFetch({ ...formatData, ORDER_ID: Number(idOrder) }));
            };

            if (newClaim) {
                if (idOrder) {
                    try {
                        const claims = await getOrderClaims(idOrder);
                        if (claims.length > 0) {
                            setConfirmParams(
                                buildClaimExistsDialogParams({
                                    orderId: idOrder,
                                    eshopOrderId: claims[0]?.ESHOP_ORDER_ID || '',
                                    claims,
                                    canCreateMultiple: permissionCreateMultipleClaims,
                                    onConfirm: submitCreate,
                                })
                            );
                            return false;
                        }
                    } catch (error) {
                        console.error('Error checking existing claims:', error);
                    }
                }
                submitCreate();
            } else {
                saveInFlightRef.current = true;
                dispatch(updateClaimDetailFetch({ idClaim, data: formatData }));
            }

            return false;
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
            setEditErrors(newFormErrors);
            return true;
        }

    };

    return (
        <>
            <TopBlock
                data={data}
                idClaim={idClaim}
                newClaim={newClaim}
                disableEdit={disableEdit}
                loading={!!loading}
                handleCreateOrder={handleCreateOrder}
                handleSave={handleSave}
                activeTab={activeTab}
                onChangeTab={(value) => setActiveTab(value)}
                permissionCreateOrder={permissionCreateOrder}
                showErrorLogTab={showErrorLogTab}
                canEditErrorLog={canEditErrorLog}
            />
            {loading && <Loading />}
            <RenderComponents
                data={data}
                onChange={onChange}
                onChangeProduct={onChangeProduct}
                editData={editData}
                editErrors={editErrors}
                newClaim={newClaim}
                disableEdit={disableEdit}
                permissionEditAll={permissionEditAll}
                isAttributeEditDisabled={isAttributeEditDisabled}
                allowedStatuses={allowedStatuses}
                claimStatusCodes={claimStatusCodes}
                claimStatusCodesObject={claimStatusCodesObject}
                claimReasonCodes={claimReasonCodes}
                claimReasonCodesObject={claimReasonCodesObject}
                claimsResolverCodes={claimsResolverCodes}
                claimsResolverCodesObject={claimsResolverCodesObject}
                activeTab={activeTab}
                changelog={changelog}
                skladonUser={skladonUser}
                currencyCodes={currencyCodes}
                formRef={formRef}
                productsFormRef={productsFormRef}
                canEditProducts={canEditProducts}
                lineStateCodesObject={lineStateCodesObject}
                boolNumberCodesObject={boolNumberCodesObject}
                canEditErrorLog={canEditErrorLog}
                errorLogFormRef={errorLogFormRef}
            />

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                    okLabel={confirmParams.okLabel}
                />
            )}
        </>
    );
};

export default ClaimDetailContainer;
